import { useState, useEffect } from 'react';
import { api } from '../services/api.ts';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, Clock, History } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface HostApplication {
  application_id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  business_name: string;
  description?: string;
  experience?: string;
  event_types?: string[];
  social_links?: Record<string, string>;
  website?: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

interface ArtistApplication {
  request_id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  stage_name: string;
  bio?: string;
  genres?: string[];
  social_links?: Record<string, string>;
  profile_image?: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

export default function AdminDashboard() {
  const [hostApps, setHostApps] = useState<HostApplication[]>([]);
  const [artistApps, setArtistApps] = useState<ArtistApplication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('hosts');

  const [allHostApps, setAllHostApps] = useState<HostApplication[]>([]);
  const [allArtistApps, setAllArtistApps] = useState<ArtistApplication[]>([]);
  const [hostHistory, setHostHistory] = useState<HostApplication[]>([]);
  const [artistHistory, setArtistHistory] = useState<ArtistApplication[]>([]);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setIsLoading(true);
      const [pendingHosts, pendingArtists, approvedHosts, approvedArtists, rejectedHosts, rejectedArtists] = await Promise.all([
        api.getHostApplications('pending'),
        api.getArtistApplications('pending'),
        api.getHostApplications('approved'),
        api.getArtistApplications('approved'),
        api.getHostApplications('rejected'),
        api.getArtistApplications('rejected')
      ]);
      setHostApps(pendingHosts);
      setArtistApps(pendingArtists);
      setAllHostApps([...pendingHosts, ...approvedHosts]);
      setAllArtistApps([...pendingArtists, ...approvedArtists]);
      // History includes approved and rejected (processed applications)
      setHostHistory([...approvedHosts, ...rejectedHosts].sort((a, b) => 
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      ));
      setArtistHistory([...approvedArtists, ...rejectedArtists].sort((a, b) => 
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      ));
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setIsLoading(false);
    }
  };

  const handleHostApplication = async (id: string, action: 'approve' | 'reject') => {
    try {
      await api.reviewHostApplication(id, action);
      toast.success(`Application ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      await loadApplications();
    } catch (error: any) {
      toast.error(error.message || `Failed to ${action} application`);
    }
  };

  const handleArtistApplication = async (id: string, action: 'approve' | 'reject') => {
    try {
      await api.reviewArtistApplication(id, action);
      toast.success(`Application ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      await loadApplications();
    } catch (error: any) {
      toast.error(error.message || `Failed to ${action} application`);
    }
  };

  const renderHostApplications = (apps: HostApplication[]) => {
    if (apps.length === 0) {
      return (
        <Card className="glass border-white/10 p-8 md:p-12 text-center">
          <Clock className="h-12 w-12 md:h-16 md:w-16 text-gray-600 mx-auto mb-3 md:mb-4" />
          <p className="text-gray-400 text-base md:text-lg">No pending host applications</p>
        </Card>
      );
    }

    return (
      <div className="space-y-3 md:space-y-4">
        {apps.map((app) => (
          <Card key={app.application_id} className="glass border-white/10" data-testid={`host-app-${app.application_id}`}>
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3 md:mb-4">
                <div className="space-y-1 min-w-0">
                  <h3 className="font-heading font-bold text-lg md:text-xl truncate">{app.business_name}</h3>
                  <p className="text-xs md:text-sm text-gray-400">Applied by: {app.user_name}</p>
                  <p className="text-xs text-gray-500 truncate">{app.user_email}</p>
                  <p className="text-xs text-gray-500">
                    Submitted: {app.created_at ? format(new Date(app.created_at), 'MMM dd, yyyy') : 'N/A'}
                  </p>
                </div>
                <Badge variant={app.status === 'pending' ? 'secondary' : 'default'} className="self-start">
                  {app.status}
                </Badge>
              </div>

              {app.description && (
                <div className="mb-3 md:mb-4">
                  <p className="text-xs md:text-sm font-medium text-gray-300 mb-1">Description:</p>
                  <p className="text-xs md:text-sm text-gray-400 line-clamp-3">{app.description}</p>
                </div>
              )}

              {app.experience && (
                <div className="mb-3 md:mb-4">
                  <p className="text-xs md:text-sm font-medium text-gray-300 mb-1">Experience:</p>
                  <p className="text-xs md:text-sm text-gray-400 line-clamp-2">{app.experience}</p>
                </div>
              )}

              {app.status === 'pending' && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={() => handleHostApplication(app.application_id, 'approve')}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-sm"
                    data-testid={`approve-host-${app.application_id}`}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    onClick={() => handleHostApplication(app.application_id, 'reject')}
                    variant="destructive"
                    className="flex-1 text-sm"
                    data-testid={`reject-host-${app.application_id}`}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const renderArtistApplications = (apps: ArtistApplication[]) => {
    if (apps.length === 0) {
      return (
        <Card className="glass border-white/10 p-12 text-center">
          <Clock className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No pending artist applications</p>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {apps.map((app) => (
          <Card key={app.request_id} className="glass border-white/10" data-testid={`artist-app-${app.request_id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="space-y-1">
                  <h3 className="font-heading font-bold text-xl">{app.stage_name}</h3>
                  <p className="text-sm text-gray-400">Applied by: {app.user_name}</p>
                  <p className="text-xs text-gray-500">{app.user_email}</p>
                  <p className="text-xs text-gray-500">
                    Submitted: {app.created_at ? format(new Date(app.created_at), 'MMM dd, yyyy') : 'N/A'}
                  </p>
                </div>
                <Badge variant={app.status === 'pending' ? 'secondary' : 'default'}>
                  {app.status}
                </Badge>
              </div>

              {app.bio && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-300 mb-1">Bio:</p>
                  <p className="text-sm text-gray-400">{app.bio}</p>
                </div>
              )}

              {app.genres && app.genres.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-300 mb-1">Genres:</p>
                  <div className="flex flex-wrap gap-1">
                    {app.genres.map((genre, index) => (
                      <Badge key={index} variant="outline" className="text-xs">{genre}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {app.status === 'pending' && (
                <div className="flex space-x-2">
                  <Button
                    onClick={() => handleArtistApplication(app.request_id, 'approve')}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid={`approve-artist-${app.request_id}`}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    onClick={() => handleArtistApplication(app.request_id, 'reject')}
                    variant="destructive"
                    className="flex-1"
                    data-testid={`reject-artist-${app.request_id}`}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const renderHostHistory = (apps: HostApplication[]) => {
    if (apps.length === 0) {
      return (
        <Card className="glass border-white/10 p-12 text-center">
          <History className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No processed host applications yet</p>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {apps.map((app) => (
          <Card key={app.application_id} className="glass border-white/10" data-testid={`host-history-${app.application_id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="space-y-1">
                  <h3 className="font-heading font-bold text-xl">{app.business_name}</h3>
                  <p className="text-sm text-gray-400">Applied by: {app.user_name}</p>
                  <p className="text-xs text-gray-500">{app.user_email}</p>
                  <p className="text-xs text-gray-500">
                    Submitted: {app.created_at ? format(new Date(app.created_at), 'MMM dd, yyyy') : 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Processed: {app.updated_at ? format(new Date(app.updated_at), 'MMM dd, yyyy HH:mm') : 'N/A'}
                  </p>
                </div>
                <Badge 
                  variant={app.status === 'approved' ? 'default' : 'destructive'}
                  className={app.status === 'approved' ? 'bg-green-600' : ''}
                >
                  {app.status === 'approved' ? (
                    <><CheckCircle className="h-3 w-3 mr-1" /> Approved</>
                  ) : (
                    <><XCircle className="h-3 w-3 mr-1" /> Rejected</>
                  )}
                </Badge>
              </div>

              {app.description && (
                <div className="mb-2">
                  <p className="text-sm font-medium text-gray-300 mb-1">Description:</p>
                  <p className="text-sm text-gray-400">{app.description}</p>
                </div>
              )}

              {app.experience && (
                <div>
                  <p className="text-sm font-medium text-gray-300 mb-1">Experience:</p>
                  <p className="text-sm text-gray-400">{app.experience}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const renderArtistHistory = (apps: ArtistApplication[]) => {
    if (apps.length === 0) {
      return (
        <Card className="glass border-white/10 p-12 text-center">
          <History className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No processed artist applications yet</p>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {apps.map((app) => (
          <Card key={app.request_id} className="glass border-white/10" data-testid={`artist-history-${app.request_id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="space-y-1">
                  <h3 className="font-heading font-bold text-xl">{app.stage_name}</h3>
                  <p className="text-sm text-gray-400">Applied by: {app.user_name}</p>
                  <p className="text-xs text-gray-500">{app.user_email}</p>
                  <p className="text-xs text-gray-500">
                    Submitted: {app.created_at ? format(new Date(app.created_at), 'MMM dd, yyyy') : 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Processed: {app.updated_at ? format(new Date(app.updated_at), 'MMM dd, yyyy HH:mm') : 'N/A'}
                  </p>
                </div>
                <Badge 
                  variant={app.status === 'approved' ? 'default' : 'destructive'}
                  className={app.status === 'approved' ? 'bg-green-600' : ''}
                >
                  {app.status === 'approved' ? (
                    <><CheckCircle className="h-3 w-3 mr-1" /> Approved</>
                  ) : (
                    <><XCircle className="h-3 w-3 mr-1" /> Rejected</>
                  )}
                </Badge>
              </div>

              {app.bio && (
                <div className="mb-2">
                  <p className="text-sm font-medium text-gray-300 mb-1">Bio:</p>
                  <p className="text-sm text-gray-400">{app.bio}</p>
                </div>
              )}

              {app.genres && app.genres.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-300 mb-1">Genres:</p>
                  <div className="flex flex-wrap gap-1">
                    {app.genres.map((genre, index) => (
                      <Badge key={index} variant="outline" className="text-xs">{genre}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4 md:space-y-8 pb-20 md:pb-8 px-2 md:px-0">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold tracking-tight">
          Admin Dashboard
        </h1>
        <p className="text-sm md:text-lg text-gray-400 mt-1 md:mt-2">
          Manage applications and platform settings
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
        <Card className="glass border-white/10" data-testid="pending-hosts-stat">
          <CardContent className="p-3 md:p-4 text-center">
            <p className="text-xl md:text-2xl font-bold">{hostApps.length}</p>
            <p className="text-xs md:text-sm text-gray-400">Pending Hosts</p>
          </CardContent>
        </Card>
        <Card className="glass border-white/10" data-testid="pending-artists-stat">
          <CardContent className="p-3 md:p-4 text-center">
            <p className="text-xl md:text-2xl font-bold">{artistApps.length}</p>
            <p className="text-xs md:text-sm text-gray-400">Pending Artists</p>
          </CardContent>
        </Card>
        <Card className="glass border-white/10" data-testid="approved-hosts-stat">
          <CardContent className="p-3 md:p-4 text-center">
            <p className="text-xl md:text-2xl font-bold">{allHostApps.filter(a => a.status === 'approved').length}</p>
            <p className="text-xs md:text-sm text-gray-400">Approved Hosts</p>
          </CardContent>
        </Card>
        <Card className="glass border-white/10" data-testid="approved-artists-stat">
          <CardContent className="p-3 md:p-4 text-center">
            <p className="text-xl md:text-2xl font-bold">{allArtistApps.filter(a => a.status === 'approved').length}</p>
            <p className="text-xs md:text-sm text-gray-400">Approved Artists</p>
          </CardContent>
        </Card>
      </div>

      {/* Applications Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="glass border-white/10 flex flex-wrap gap-1 h-auto p-1">
          <TabsTrigger value="hosts" data-testid="hosts-tab" className="text-xs md:text-sm px-2 md:px-3 py-1.5">
            Hosts ({hostApps.length})
          </TabsTrigger>
          <TabsTrigger value="artists" data-testid="artists-tab" className="text-xs md:text-sm px-2 md:px-3 py-1.5">
            Artists ({artistApps.length})
          </TabsTrigger>
          <TabsTrigger value="host-history" data-testid="host-history-tab" className="text-xs md:text-sm px-2 md:px-3 py-1.5">
            <History className="h-3 w-3 md:h-4 md:w-4 mr-1" />
            <span className="hidden sm:inline">Host </span>History
          </TabsTrigger>
          <TabsTrigger value="artist-history" data-testid="artist-history-tab" className="text-xs md:text-sm px-2 md:px-3 py-1.5">
            <History className="h-3 w-3 md:h-4 md:w-4 mr-1" />
            <span className="hidden sm:inline">Artist </span>History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="hosts" className="mt-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <Card key={i} className="glass border-white/10 h-32 animate-pulse" />
              ))}
            </div>
          ) : (
            renderHostApplications(hostApps)
          )}
        </TabsContent>

        <TabsContent value="artists" className="mt-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <Card key={i} className="glass border-white/10 h-32 animate-pulse" />
              ))}
            </div>
          ) : (
            renderArtistApplications(artistApps)
          )}
        </TabsContent>

        <TabsContent value="host-history" className="mt-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <Card key={i} className="glass border-white/10 h-32 animate-pulse" />
              ))}
            </div>
          ) : (
            renderHostHistory(hostHistory)
          )}
        </TabsContent>

        <TabsContent value="artist-history" className="mt-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <Card key={i} className="glass border-white/10 h-32 animate-pulse" />
              ))}
            </div>
          ) : (
            renderArtistHistory(artistHistory)
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
